import { useState, useEffect } from "react";
import { Card, Button, Label, SectionDivider } from "../components/Primitives";
import { BarChart } from "../components/Charts";
import { useCardioLog, useCardioHistory, useDailyTracking } from "../hooks/useApi";

const CARDIO_TYPES = [
  { id: "walk", label: "Walk", unit: "min" },
  { id: "run", label: "Run", unit: "min" },
  { id: "cycle", label: "Cycle", unit: "min" },
  { id: "swim", label: "Swim", unit: "min" },
  { id: "hiit", label: "HIIT", unit: "min" },
  { id: "stairs", label: "Stairs", unit: "min" },
  { id: "row", label: "Row", unit: "min" },
  { id: "sport", label: "Sport", unit: "min" },
];

function CardioIcon({ type, color, size = 20 }) {
  const s = { width: size, height: size, display: "block" };
  const p = { fill: "none", stroke: color, strokeWidth: 1.5, strokeLinecap: "round", strokeLinejoin: "round" };
  switch (type) {
    case "walk": return <svg viewBox="0 0 24 24" style={s} {...p}><circle cx="12" cy="4" r="2"/><path d="M14 10l-1.5 7.5L15 22M10 10l1.5 7.5L9 22M9 10l-2 4M15 10l2 4"/></svg>;
    case "run": return <svg viewBox="0 0 24 24" style={s} {...p}><circle cx="13" cy="3" r="2"/><path d="M17 11l-2-4-3 1-2 3h-3M11 8l-1 9 5 4M11 17l-4 4"/></svg>;
    case "cycle": return <svg viewBox="0 0 24 24" style={s} {...p}><circle cx="6" cy="17" r="3"/><circle cx="18" cy="17" r="3"/><path d="M6 17l3-7h4l3 7M13 10l-1-4h3"/></svg>;
    case "swim": return <svg viewBox="0 0 24 24" style={s} {...p}><circle cx="8" cy="6" r="2"/><path d="M2 18c1-1 2-2 4-2s3 2 4 2 2-2 4-2 3 2 4 2M6 12l4-2 3 3"/></svg>;
    case "hiit": return <svg viewBox="0 0 24 24" style={s} {...p}><path d="M13 2L3 14h9l-1 8 10-12h-9l1-8z"/></svg>;
    case "stairs": return <svg viewBox="0 0 24 24" style={s} {...p}><path d="M4 20h4v-4h4v-4h4V8h4"/></svg>;
    case "row": return <svg viewBox="0 0 24 24" style={s} {...p}><path d="M4 18l4-8h12M8 10l-2 4M16 10v4M20 10v4"/><path d="M2 20c2-2 4-2 6-2s4 2 6 2 4-2 6-2"/></svg>;
    case "sport": return <svg viewBox="0 0 24 24" style={s} {...p}><circle cx="12" cy="12" r="9"/><path d="M12 3c-1.5 3-1.5 6 0 9s1.5 6 0 9M3 12h18M4 7h16M4 17h16"/></svg>;
    default: return null;
  }
}

const INTENSITY = ["Low", "Moderate", "High", "Max"];

function getTodayKey() {
  return new Date().toISOString().split("T")[0];
}

export default function CardioLog({ C, onBack }) {
  const todayKey = getTodayKey();
  const { sessions, loading, logSession } = useCardioLog(todayKey);
  const { logs: historyLogs } = useCardioHistory(30);
  const { steps: apiSteps, update: updateTracking } = useDailyTracking(todayKey);

  const [adding, setAdding] = useState(false);
  const [newType, setNewType] = useState("walk");
  const [newDuration, setNewDuration] = useState("");
  const [newIntensity, setNewIntensity] = useState(1);
  const [newCalories, setNewCalories] = useState("");
  const [newHeartRate, setNewHeartRate] = useState("");
  const [newNotes, setNewNotes] = useState("");
  const [steps, setSteps] = useState(0);
  const [stepsInput, setStepsInput] = useState("");

  // Sync steps from daily tracking API
  useEffect(() => {
    if (apiSteps) {
      setSteps(apiSteps);
      setStepsInput(String(apiSteps));
    }
  }, [apiSteps]);

  const addSession = async () => {
    if (!newDuration || Number(newDuration) <= 0) return;
    await logSession({
      cardioType: newType,
      durationMinutes: Number(newDuration),
      intensity: INTENSITY[newIntensity],
      caloriesBurned: newCalories ? Number(newCalories) : null,
      avgHeartRate: newHeartRate ? Number(newHeartRate) : null,
      notes: newNotes || null,
    });
    setAdding(false);
    setNewDuration("");
    setNewCalories("");
    setNewHeartRate("");
    setNewNotes("");
  };

  const saveSteps = () => {
    const val = Number(stepsInput) || 0;
    setSteps(val);
    updateTracking({ steps: val }).catch(e => console.warn("[Forge] saveSteps:", e));
  };

  const todayMins = sessions.reduce((a, s) => a + (s.durationMinutes || 0), 0);
  const todayCals = sessions.reduce((a, s) => a + (s.caloriesBurned || 0), 0);

  // Weekly chart from history
  const dayMs = 86400000;
  const weekData = [];
  for (let i = 6; i >= 0; i--) {
    const d = new Date(Date.now() - i * dayMs);
    const dk = d.toISOString().split("T")[0];
    const dayLogs = dk === todayKey
      ? sessions
      : historyLogs.filter(l => l.date === dk);
    const mins = dayLogs.reduce((a, s) => a + (s.durationMinutes || 0), 0);
    weekData.push({ value: mins, label: ["Sun","Mon","Tue","Wed","Thu","Fri","Sat"][d.getDay()] });
  }

  // Group history by date for display
  const historyByDate = {};
  historyLogs.forEach(l => {
    if (!historyByDate[l.date]) historyByDate[l.date] = [];
    historyByDate[l.date].push(l);
  });
  const historyDates = Object.keys(historyByDate).sort().reverse().filter(d => d !== todayKey);

  if (loading) {
    return (
      <div style={{ textAlign: "center", padding: "60px 20px" }}>
        <div style={{ fontSize: 'var(--fs-lg)', color: C.text3, fontFamily: "var(--m)" }}>Loading...</div>
      </div>
    );
  }

  return (
    <div>
      {onBack && (
        <button onClick={onBack} style={{
          display: "flex", alignItems: "center", gap: 'var(--gap-sm)', background: "none", border: "none",
          color: C.accent, fontSize: 'var(--fs-body)', fontFamily: "var(--m)", fontWeight: 600,
          letterSpacing: ".06em", cursor: "pointer", padding: "0 0 16px", marginTop: -4,
        }}>
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round"><path d="M15 18l-6-6 6-6" /></svg>
          BACK
        </button>
      )}

      <div style={{ marginBottom: 24 }}>
        <div style={{ fontSize: 'var(--fs-h1)', fontWeight: 700, color: C.text1, fontFamily: "var(--d)", marginBottom: 4 }}>Cardio & Conditioning</div>
        <div style={{ fontSize: 'var(--fs-body)', color: C.text4, fontFamily: "var(--m)" }}>Track all cardiovascular activity, steps, and active recovery</div>
      </div>

      {/* Today Summary */}
      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 'var(--gap-md)', marginBottom: 20 }}>
        {[
          { v: todayMins, l: "MINUTES" },
          { v: todayCals || 0, l: "CALORIES" },
          { v: steps || 0, l: "STEPS" },
        ].map(({ v, l }) => (
          <Card key={l} C={C} style={{ textAlign: "center", padding: "20px 8px" }}>
            <div style={{ fontSize: 'var(--fs-h1)', fontWeight: 700, color: C.accent, fontFamily: "var(--m)", textShadow: `0 0 20px ${C.accent}30` }}>{v}</div>
            <div style={{ fontSize: 'var(--fs-micro)', color: C.text4, fontFamily: "var(--m)", letterSpacing: ".14em", marginTop: 6 }}>{l}</div>
          </Card>
        ))}
      </div>

      {/* Steps Input */}
      <Card C={C} style={{ padding: 'var(--card-pad-lg)', marginBottom: 16 }}>
        <div style={{ display: "flex", alignItems: "center", gap: 'var(--sp-lg)' }}>
          <div style={{ flex: 1 }}>
            <div style={{ fontSize: 'var(--fs-2xs)', color: C.text4, fontFamily: "var(--m)", letterSpacing: ".12em", marginBottom: 6 }}>DAILY STEPS</div>
            <input value={stepsInput} onChange={(e) => setStepsInput(e.target.value.replace(/\D/g, ""))} onBlur={saveSteps} placeholder="0" type="number" inputMode="numeric"
              style={{ width: "100%", padding: "10px 12px", background: C.structGlass, border: `1px solid ${C.structBorderHover}`, borderRadius: 8, color: C.text1, fontSize: 'var(--fs-xl)', fontWeight: 600, fontFamily: "var(--m)", outline: "none" }} />
          </div>
          <div style={{ textAlign: "center", paddingTop: 16 }}>
            <div style={{ fontSize: 'var(--fs-xs)', color: steps >= 10000 ? C.secondary : C.text4, fontFamily: "var(--m)" }}>
              {steps >= 10000 ? "Goal hit" : `${Math.max(0, 10000 - (steps || 0)).toLocaleString()} to go`}
            </div>
          </div>
        </div>
      </Card>

      {/* Today's Sessions */}
      <Label C={C}>Today's Sessions</Label>
      {sessions.length === 0 && !adding && (
        <Card C={C} style={{ padding: 'var(--card-pad-lg)', textAlign: "center", marginBottom: 8 }}>
          <div style={{ fontSize: 'var(--fs-base)', color: C.text4, fontFamily: "var(--m)" }}>No cardio logged today</div>
        </Card>
      )}
      {sessions.map((s, i) => {
        const typeObj = CARDIO_TYPES.find(t => t.id === s.cardioType) || CARDIO_TYPES[0];
        return (
          <Card key={s.id || i} C={C} style={{ display: "flex", alignItems: "center", gap: 14, padding: "16px 18px" }}>
            <div style={{ width: 32, height: 32, display: "flex", alignItems: "center", justifyContent: "center", background: C.accent008, borderRadius: 10, padding: 6, border: `1px solid ${C.accent015}` }}><CardioIcon type={typeObj.id} color={C.accent} size={20} /></div>
            <div style={{ flex: 1 }}>
              <div style={{ fontSize: 'var(--fs-md)', fontWeight: 600, color: C.text1 }}>{typeObj.label}</div>
              <div style={{ fontSize: 'var(--fs-sm)', color: C.text4, fontFamily: "var(--m)", marginTop: 2 }}>
                {s.durationMinutes} min · {s.intensity}{s.caloriesBurned ? ` · ${s.caloriesBurned} cal` : ""}{s.avgHeartRate ? ` · ${s.avgHeartRate} bpm` : ""}
              </div>
              {s.notes && <div style={{ fontSize: 'var(--fs-xs)', color: C.text3, fontFamily: "var(--m)", marginTop: 3, fontStyle: "italic" }}>{s.notes}</div>}
            </div>
          </Card>
        );
      })}

      {/* Add Session Form */}
      {adding ? (
        <Card C={C} style={{ padding: 'var(--card-pad)', marginBottom: 8 }}>
          <Label C={C}>LOG CARDIO SESSION</Label>
          {/* Type selector */}
          <div style={{ display: "grid", gridTemplateColumns: "repeat(4, 1fr)", gap: 8, marginBottom: 18 }}>
            {CARDIO_TYPES.map(t => (
              <button key={t.id} onClick={() => setNewType(t.id)} style={{
                padding: "14px 4px", background: newType === t.id ? C.accent010 : C.structGlass,
                border: `1.5px solid ${newType === t.id ? C.accent : C.structBorderHover}`,
                borderRadius: 10, cursor: "pointer", textAlign: "center", transition: "all .15s",
                boxShadow: newType === t.id ? `0 0 12px ${C.accent}15` : "none",
              }}>
                <div style={{ display: "flex", justifyContent: "center", marginBottom: 6 }}><CardioIcon type={t.id} color={newType === t.id ? C.accent : C.text4} size={24} /></div>
                <div style={{ fontSize: 'var(--fs-2xs)', color: newType === t.id ? C.accent : C.text4, fontFamily: "var(--m)", fontWeight: newType === t.id ? 600 : 400, letterSpacing: ".06em" }}>{t.label}</div>
              </button>
            ))}
          </div>
          {/* Duration + Intensity */}
          <div style={{ display: "flex", gap: 'var(--gap-md)', marginBottom: 12 }}>
            <div style={{ flex: 1 }}>
              <div style={{ fontSize: 'var(--fs-2xs)', color: C.text4, fontFamily: "var(--m)", letterSpacing: ".12em", marginBottom: 4 }}>DURATION (MIN)</div>
              <input value={newDuration} onChange={(e) => setNewDuration(e.target.value.replace(/\D/g, ""))} placeholder="30" type="number" inputMode="numeric"
                style={{ width: "100%", padding: "12px", background: C.structGlass, border: `1px solid ${C.structBorderHover}`, borderRadius: 10, color: C.text1, fontSize: 'var(--fs-xl)', fontWeight: 600, fontFamily: "var(--m)", textAlign: "center", outline: "none", boxSizing: "border-box" }} />
            </div>
            <div style={{ flex: 1 }}>
              <div style={{ fontSize: 'var(--fs-2xs)', color: C.text4, fontFamily: "var(--m)", letterSpacing: ".12em", marginBottom: 4 }}>INTENSITY</div>
              <div style={{ display: "flex", gap: 3 }}>
                {INTENSITY.map((label, i) => (
                  <button key={i} onClick={() => setNewIntensity(i)} style={{
                    flex: 1, padding: "12px 2px", background: newIntensity === i ? C.accent010 : C.structGlass,
                    border: `1px solid ${newIntensity === i ? C.accent : C.structBorderHover}`,
                    borderRadius: 8, color: newIntensity === i ? C.accent : C.text4, fontSize: 'var(--fs-2xs)', fontFamily: "var(--m)", fontWeight: newIntensity === i ? 600 : 400, cursor: "pointer", transition: "all .15s", letterSpacing: ".04em",
                  }}>{label.slice(0, 3)}</button>
                ))}
              </div>
            </div>
          </div>
          {/* Optional: Calories + HR */}
          <div style={{ display: "flex", gap: 'var(--gap-md)', marginBottom: 12 }}>
            <div style={{ flex: 1 }}>
              <div style={{ fontSize: 'var(--fs-2xs)', color: C.text4, fontFamily: "var(--m)", letterSpacing: ".12em", marginBottom: 4 }}>CALORIES (OPT)</div>
              <input value={newCalories} onChange={(e) => setNewCalories(e.target.value.replace(/\D/g, ""))} placeholder="" type="number" inputMode="numeric"
                style={{ width: "100%", padding: "12px", background: C.structGlass, border: `1px solid ${C.structBorderHover}`, borderRadius: 10, color: C.text1, fontSize: 'var(--fs-lg)', fontFamily: "var(--m)", textAlign: "center", outline: "none", boxSizing: "border-box" }} />
            </div>
            <div style={{ flex: 1 }}>
              <div style={{ fontSize: 'var(--fs-2xs)', color: C.text4, fontFamily: "var(--m)", letterSpacing: ".12em", marginBottom: 4 }}>AVG HR (OPT)</div>
              <input value={newHeartRate} onChange={(e) => setNewHeartRate(e.target.value.replace(/\D/g, ""))} placeholder="" type="number" inputMode="numeric"
                style={{ width: "100%", padding: "12px", background: C.structGlass, border: `1px solid ${C.structBorderHover}`, borderRadius: 10, color: C.text1, fontSize: 'var(--fs-lg)', fontFamily: "var(--m)", textAlign: "center", outline: "none", boxSizing: "border-box" }} />
            </div>
          </div>
          {/* Notes */}
          <input value={newNotes} onChange={(e) => setNewNotes(e.target.value)} placeholder="Notes (optional)" style={{
            width: "100%", padding: "12px 14px", background: C.structGlass, border: `1px solid ${C.structBorderHover}`,
            borderRadius: 10, color: C.text3, fontSize: 'var(--fs-body)', fontFamily: "var(--m)", outline: "none", marginBottom: 18, boxSizing: "border-box",
          }} />
          <div style={{ display: "flex", gap: 'var(--gap-md)' }}>
            <Button C={C} onClick={addSession} style={{ flex: 1 }}>LOG SESSION</Button>
            <Button C={C} onClick={() => setAdding(false)} variant="ghost" style={{ flex: 1 }}>CANCEL</Button>
          </div>
        </Card>
      ) : (
        <Button C={C} onClick={() => setAdding(true)} variant="ghost" style={{ marginBottom: 8 }}>+ LOG CARDIO SESSION</Button>
      )}

      <SectionDivider C={C} />

      {/* Weekly Chart */}
      <Label C={C}>This Week (Minutes)</Label>
      <Card C={C} style={{ padding: 'var(--card-pad-lg)', marginBottom: 8 }}>
        <BarChart data={weekData} C={C} height={120} />
        <div style={{ fontSize: 'var(--fs-xs)', color: C.text4, fontFamily: "var(--m)", marginTop: 10, textAlign: "center" }}>
          {weekData.reduce((a, d) => a + d.value, 0)} total minutes this week
        </div>
      </Card>

      {/* History */}
      {historyDates.length > 0 && (
        <>
          <SectionDivider C={C} />
          <Label C={C}>Recent History</Label>
          {historyDates.slice(0, 7).map((date, i) => {
            const dayLogs = historyByDate[date];
            const totalMin = dayLogs.reduce((a, s) => a + (s.durationMinutes || 0), 0);
            const totalCal = dayLogs.reduce((a, s) => a + (s.caloriesBurned || 0), 0);
            return (
              <Card key={i} C={C} style={{ display: "flex", alignItems: "center", gap: 14, padding: "14px 18px" }}>
                <div style={{ width: 32, height: 32, display: "flex", alignItems: "center", justifyContent: "center", background: C.accent005, borderRadius: 10, border: `1px solid ${C.accent010}` }}>
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke={C.accent} strokeWidth="1.5" strokeLinecap="round"><rect x="3" y="4" width="18" height="18" rx="2" /><line x1="16" y1="2" x2="16" y2="6" /><line x1="8" y1="2" x2="8" y2="6" /><line x1="3" y1="10" x2="21" y2="10" /></svg>
                </div>
                <div style={{ flex: 1 }}>
                  <div style={{ fontSize: 'var(--fs-base)', fontWeight: 600, color: C.text1 }}>{new Date(date + "T12:00:00").toLocaleDateString("en-US", { weekday: "short", month: "short", day: "numeric" })}</div>
                  <div style={{ fontSize: 'var(--fs-sm)', color: C.text4, fontFamily: "var(--m)", marginTop: 2 }}>
                    {dayLogs.length} session{dayLogs.length !== 1 ? "s" : ""} · {totalMin} min{totalCal ? ` · ${totalCal} cal` : ""}
                  </div>
                </div>
              </Card>
            );
          })}
        </>
      )}
    </div>
  );
}
